// Formulario de compra (/eventos/:id/comprar): resumen en vivo del total y bloques por asistente.
// La página se carga sin Turbo (data-turbo="false"), pero turbo:load también dispara en cargas completas.

const money = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' });
const formatMoney = (amount) => `${money.format(amount)} MXN`;

const selectedRows = (root) =>
  Array.from(root.querySelectorAll('[data-price-row]'))
    .map((row) => ({
      id: row.dataset.priceId,
      name: row.dataset.priceName,
      amount: parseFloat(row.dataset.priceAmount) || 0,
      quantity: parseInt(row.querySelector('[data-quantity]')?.value || '0', 10) || 0,
    }))
    .filter((row) => row.quantity > 0);

const updateSummary = (root) => {
  const summary = root.querySelector('[data-summary]');
  if (!summary) return;

  const rows = selectedRows(root);
  const subtotal = rows.reduce((sum, row) => sum + row.amount * row.quantity, 0);
  const feePercentage = parseFloat(root.dataset.feePercentage) || 0;
  const ivaRate = parseFloat(root.dataset.feeIvaRate) || 0;
  // Mismo redondeo que Event#service_fee_for: cargo base y su IVA, cada uno a centavos.
  const fee = Math.round(subtotal * feePercentage) / 100;
  const feeIva = Math.round(fee * ivaRate * 100) / 100;

  summary.hidden = rows.length === 0;
  renderLines(root, rows);
  summary.querySelector('[data-summary-subtotal]').textContent = formatMoney(subtotal);
  const feeNode = summary.querySelector('[data-summary-fee]');
  if (feeNode) feeNode.textContent = formatMoney(fee);
  const feeIvaNode = summary.querySelector('[data-summary-fee-iva]');
  if (feeIvaNode) feeIvaNode.textContent = formatMoney(feeIva);
  const total = formatMoney(subtotal + fee + feeIva);
  summary.querySelector('[data-summary-total]').textContent = total;
  const barTotal = root.querySelector('[data-summary-bar-total]');
  if (barTotal) barTotal.textContent = total;
};

// Líneas del resumen ("2 × General  $200.00 MXN").
const renderLines = (root, rows) => {
  const list = root.querySelector('[data-summary-lines]');
  if (!list) return;

  list.querySelectorAll('[data-summary-line]').forEach((line) => line.remove());
  const empty = list.querySelector('[data-summary-empty]');
  if (empty) empty.hidden = rows.length > 0;

  rows.forEach((row) => {
    const line = document.createElement('li');
    line.dataset.summaryLine = '';
    const label = document.createElement('span');
    label.textContent = `${row.quantity} × ${row.name}`;
    const amount = document.createElement('span');
    amount.textContent = formatMoney(row.amount * row.quantity);
    line.append(label, amount);
    list.appendChild(line);
  });
};

// Botones −/+ de cada tipo de boleto: ajustan el input numérico respetando min y max.
const syncSteppers = (root) => {
  root.querySelectorAll('[data-price-row]').forEach((row) => {
    const input = row.querySelector('[data-quantity]');
    if (!input) return;
    const value = parseInt(input.value || '0', 10) || 0;
    const max = parseInt(input.max || '0', 10);
    row.classList.toggle('--selected', value > 0);
    row.querySelector('[data-step="-1"]')?.toggleAttribute('disabled', input.disabled || value <= 0);
    row.querySelector('[data-step="1"]')?.toggleAttribute('disabled', input.disabled || value >= max);
  });
};

const bindSteppers = (root, onChange) => {
  root.querySelectorAll('[data-price-row]').forEach((row) => {
    const input = row.querySelector('[data-quantity]');
    if (!input) return;

    row.querySelectorAll('[data-step]').forEach((button) => {
      button.addEventListener('click', () => {
        const max = parseInt(input.max || '0', 10);
        const next = (parseInt(input.value || '0', 10) || 0) + parseInt(button.dataset.step, 10);
        input.value = Math.min(Math.max(next, 0), max);
        onChange();
      });
    });

    input.addEventListener('input', onChange);
    input.addEventListener('change', () => {
      const max = parseInt(input.max || '0', 10);
      input.value = Math.min(Math.max(parseInt(input.value || '0', 10) || 0, 0), max);
      onChange();
    });
  });
};

// Barra móvil: lleva al resumen (donde están la aceptación legal y el botón de pago) y se oculta cuando se ve.
const bindMobileBar = (root) => {
  const bar = root.querySelector('[data-order-bar]');
  const summary = root.querySelector('.summary-card');
  if (!bar || !summary) return;

  bar.querySelector('[data-scroll-to-submit]')?.addEventListener('click', () => {
    summary.scrollIntoView({ behavior: 'smooth', block: 'center' });
  });

  if ('IntersectionObserver' in window) {
    new IntersectionObserver(([entry]) => {
      bar.classList.toggle('--hidden', entry.isIntersecting);
    }, { threshold: 0.25 }).observe(summary);
  }
};

// Qué falta para poder pagar: boletos, campos obligatorios (required, formato) y un contacto por asistente.
// Los campos deshabilitados (bloque "tú") no cuentan en checkValidity.
const missingRequirements = (root, form) => {
  if (selectedRows(root).length === 0) return 'Elige al menos un boleto para continuar.';

  const attendeeWithoutContact = Array.from(root.querySelectorAll('[data-attendee]')).some((block) => {
    if (block.hidden) return false;
    const email = block.querySelector('[data-field="email"]')?.value.trim();
    const phone = block.querySelector('[data-field="phone"]')?.value.trim();
    return !email && !phone;
  });

  if (!form.checkValidity() || attendeeWithoutContact) return 'Completa los datos obligatorios para continuar.';
  return null;
};

// Deshabilita el pago hasta que el formulario esté completo y evita órdenes duplicadas por doble clic.
const bindSubmit = (root) => {
  const form = root.querySelector('[data-order-submit]');
  const button = root.querySelector('[data-submit-button]');
  if (!form || !button) return () => {};

  // Venta cerrada o agotada: el servidor ya lo deshabilitó y así se queda.
  if (button.disabled) return () => {};

  const label = button.querySelector('[data-submit-label]');
  const original = label?.textContent;
  const hint = root.querySelector('[data-submit-hint]');

  const update = () => {
    const missing = missingRequirements(root, form);
    button.disabled = Boolean(missing);
    if (hint) {
      hint.textContent = missing || '';
      hint.hidden = !missing;
    }
  };

  form.addEventListener('input', update);
  form.addEventListener('change', update);

  form.addEventListener('submit', () => {
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    if (label) label.textContent = 'Procesando…';
  });

  // Al volver con "atrás" el navegador puede restaurar la página con el botón deshabilitado.
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    button.removeAttribute('aria-busy');
    if (label) label.textContent = original;
    update();
  });

  return update;
};

// Guarda lo capturado para no perderlo al cambiar cantidades.
const collectValues = (list) => {
  const values = {};
  list.querySelectorAll('[data-attendee]').forEach((block) => {
    const key = `${block.dataset.priceId}:${block.dataset.index}`;
    values[key] = {};
    block.querySelectorAll('[data-field]').forEach((input) => {
      values[key][input.dataset.field] = input.value;
    });
  });
  return values;
};

const initialValues = (root) => {
  try {
    const parsed = JSON.parse(root.dataset.initialAttendees || '{}');
    const values = {};
    Object.entries(parsed).forEach(([priceId, entries]) => {
      (entries || []).forEach((entry, index) => {
        values[`${priceId}:${index}`] = entry || {};
      });
    });
    return values;
  } catch (_error) {
    return {};
  }
};

const renderAttendees = (root, previous) => {
  const list = root.querySelector('[data-attendees-list]');
  const template = root.querySelector('[data-attendee-template]');
  if (!list || !template) return;

  const firstIsMine = root.querySelector('[data-first-ticket-is-mine]')?.checked;
  const rows = selectedRows(root);
  list.innerHTML = '';

  let position = 0;
  rows.forEach((row) => {
    for (let index = 0; index < row.quantity; index += 1) {
      position += 1;
      const block = template.content.firstElementChild.cloneNode(true);
      block.dataset.priceId = row.id;
      block.dataset.index = index;
      block.querySelector('[data-attendee-label]').textContent = `Boleto ${position} · ${row.name}`;

      const saved = previous[`${row.id}:${index}`] || {};
      block.querySelectorAll('[data-field]').forEach((input) => {
        input.name = `attendees[${row.id}][${index}][${input.dataset.field}]`;
        input.id = `attendee_${row.id}_${index}_${input.dataset.field}`;
        // El select de lada conserva su valor por omisión si no hay uno guardado.
        if (saved[input.dataset.field] || input.tagName !== 'SELECT') input.value = saved[input.dataset.field] || '';
        const label = input.closest('.form-field')?.querySelector('label');
        if (label) label.setAttribute('for', input.id);
      });

      if (position === 1 && firstIsMine) {
        block.hidden = true;
        block.querySelectorAll('[data-field]').forEach((input) => { input.disabled = true; });
        block.querySelector('[data-attendee-label]').textContent += ' (tú)';
      }

      list.appendChild(block);
    }
  });

  const empty = root.querySelector('[data-attendees-empty]');
  if (empty) empty.hidden = rows.length > 0;
};

const mountOrderForm = () => {
  const root = document.querySelector('[data-order-form]');
  if (!root || root.dataset.mounted) return;
  root.dataset.mounted = '1';

  const withAttendees = root.dataset.attendees === '1';
  let values = initialValues(root);

  let updateSubmit = () => {};

  const refresh = () => {
    syncSteppers(root);
    updateSummary(root);
    if (withAttendees) {
      const list = root.querySelector('[data-attendees-list]');
      if (list && list.children.length) values = { ...values, ...collectValues(list) };
      renderAttendees(root, values);
    }
    updateSubmit();
  };

  bindSteppers(root, refresh);
  bindMobileBar(root);
  updateSubmit = bindSubmit(root);
  root.querySelector('[data-first-ticket-is-mine]')?.addEventListener('change', refresh);
  refresh();
};

document.addEventListener('turbo:load', mountOrderForm);
document.addEventListener('DOMContentLoaded', mountOrderForm);
